import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Search, ShieldAlert, EyeOff, AlertTriangle, Archive, Ban, RotateCcw, FileText, ExternalLink, Loader2,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import { useAuth } from '../contexts/AuthContext';
import { BACKEND_URL } from '../lib/api';

/**
 * /admin/events — moderation control room.
 *
 * Lets admins suspend / unpublish / mark under-review / archive / cancel / restore
 * any event, with a mandatory reason for protective actions. The list shows
 * status badge, sales count, and a "has-sales" indicator to help admins decide
 * between cancel (refund flow) and unpublish (silent hide).
 */

const STATUS_META = {
  active:       { label: 'Active',          tint: 'bg-emerald-50 text-emerald-800 border-emerald-200' },
  draft:        { label: 'Draft',           tint: 'bg-stone-50 text-stone-700 border-stone-200' },
  under_review: { label: 'Under Review',    tint: 'bg-amber-50 text-amber-800 border-amber-200' },
  suspended:    { label: 'Suspended',       tint: 'bg-rose-50 text-rose-800 border-rose-200' },
  unpublished:  { label: 'Unpublished',     tint: 'bg-stone-50 text-stone-700 border-stone-200' },
  archived:     { label: 'Archived',        tint: 'bg-stone-100 text-stone-600 border-stone-200' },
  cancelled:    { label: 'Cancelled',       tint: 'bg-rose-50 text-rose-700 border-rose-200' },
  deleted:      { label: 'Deleted',         tint: 'bg-stone-100 text-stone-500 border-stone-200 line-through' },
};

const ACTIONS = [
  { key: 'under_review', label: 'Mark under review', icon: AlertTriangle, danger: false, description: 'Pause sales while you investigate. Organiser is notified.' },
  { key: 'suspend',      label: 'Suspend sales',     icon: Ban,           danger: true,  description: 'Immediately stops new ticket sales. Event stays in records.' },
  { key: 'unpublish',    label: 'Unpublish',         icon: EyeOff,        danger: true,  description: 'Remove from public listings and search. Existing tickets remain valid.' },
  { key: 'cancel',       label: 'Cancel event',      icon: Ban,           danger: true,  description: 'Mark as cancelled. Use refund workflow for affected customers.' },
  { key: 'archive',      label: 'Archive',           icon: Archive,       danger: true,  description: 'Hide entirely from public view; financial records remain intact.' },
  { key: 'restore',      label: 'Restore to active', icon: RotateCcw,     danger: false, description: 'Lift the moderation lock and republish the event.' },
];

const StatusBadge = ({ status }) => {
  const meta = STATUS_META[status] || { label: status, tint: 'bg-stone-100 text-stone-600 border-stone-200' };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider border ${meta.tint}`} data-testid={`event-status-${status}`}>
      {meta.label}
    </span>
  );
};

const AdminEventsPage = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [actionFor, setActionFor] = useState(null); // { event, action }
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!authLoading && (!user || user.role !== 'admin')) {
      navigate('/');
    }
  }, [user, authLoading, navigate]);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${BACKEND_URL}/api/admin/events`, {
        params: { status: statusFilter, search: search || undefined, limit: 200 },
        withCredentials: true,
      });
      setEvents(r.data?.events || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load events.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.role === 'admin') load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, statusFilter]);

  const filtered = useMemo(() => events, [events]);

  const submitAction = async () => {
    if (!actionFor) return;
    const reasonRequired = ['suspend', 'unpublish', 'under_review', 'archive', 'cancel'].includes(actionFor.action);
    if (reasonRequired && !reason.trim()) {
      toast.error('Please add a reason — required for this action.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/events/${actionFor.event.event_id}/moderate`,
        { action: actionFor.action, reason: reason.trim() || undefined },
        { withCredentials: true },
      );
      toast.success(`Event ${actionFor.action.replace('_', ' ')}.`);
      setActionFor(null);
      setReason('');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Action failed.');
    } finally {
      setSubmitting(false);
    }
  };

  if (authLoading || (user && user.role !== 'admin')) {
    return (
      <div className="min-h-screen flex items-center justify-center text-[#4A5D50]">Checking access…</div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAF7] py-10" data-testid="admin-events-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#C9A27A] mb-1">Admin · Event Moderation</p>
            <h1
              className="text-3xl sm:text-4xl font-semibold text-[#112318]"
              style={{ fontFamily: 'Playfair Display, serif' }}
              data-testid="admin-events-title"
            >
              Event Moderation
            </h1>
            <p className="text-sm text-[#4A5D50] mt-2 max-w-2xl">
              Suspend, unpublish, archive or cancel any event that doesn't meet VinePass standards.
              Actions are audit-logged, the organiser is notified, and events with existing sales
              are never hard-deleted.
            </p>
          </div>
        </div>

        {/* Filter row */}
        <div className="flex flex-wrap items-center gap-3 mb-6">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
            <Input
              placeholder="Search event title or organiser…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && load()}
              className="pl-10 h-10 bg-white border-[#E5E9E6]"
              data-testid="admin-events-search"
            />
          </div>
          <select
            className="h-10 px-3 rounded-md border border-[#E5E9E6] bg-white text-sm text-[#1F3D2B] focus:outline-none focus:ring-1 focus:ring-[#1F3D2B]"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            data-testid="admin-events-status-filter"
          >
            <option value="all">All statuses</option>
            <option value="active">Active</option>
            <option value="draft">Draft</option>
            <option value="under_review">Under Review</option>
            <option value="suspended">Suspended</option>
            <option value="unpublished">Unpublished</option>
            <option value="archived">Archived</option>
            <option value="cancelled">Cancelled</option>
          </select>
          <Button onClick={load} variant="outline" className="rounded-full border-[#1F3D2B] text-[#1F3D2B] hover:bg-[#1F3D2B] hover:text-white h-10 px-4">
            <Search className="w-4 h-4 mr-2" /> Apply
          </Button>
        </div>

        {/* Table */}
        <div className="bg-white border border-[#E5E9E6] rounded-2xl overflow-hidden">
          {loading ? (
            <div className="py-14 flex items-center justify-center text-[#4A5D50]">
              <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading events…
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-14 text-center text-[#4A5D50]" data-testid="admin-events-empty">
              <ShieldAlert className="w-8 h-8 text-[#4A5D50]/40 mx-auto mb-2" />
              No events match your filters.
            </div>
          ) : (
            <table className="w-full text-sm" data-testid="admin-events-table">
              <thead className="bg-[#F4F6F3] text-[#1F3D2B]">
                <tr className="text-left">
                  <th className="px-4 py-3 font-medium">Event</th>
                  <th className="px-4 py-3 font-medium">Organiser</th>
                  <th className="px-4 py-3 font-medium">Date</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium">Sales</th>
                  <th className="px-4 py-3 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((ev) => (
                  <tr
                    key={ev.event_id}
                    className="border-t border-[#E5E9E6] hover:bg-[#FAFAF7]"
                    data-testid={`admin-event-row-${ev.event_id}`}
                  >
                    <td className="px-4 py-3 align-top">
                      <a
                        href={`/events/${ev.event_id}`}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 text-[#1F3D2B] hover:text-[#172E20] font-medium"
                        title="Open public event page in new tab"
                      >
                        {ev.title} <ExternalLink className="w-3 h-3 opacity-60" />
                      </a>
                      {ev.admin_moderation?.reason && (
                        <p className="text-[11px] text-rose-700 mt-1 max-w-md">
                          <FileText className="inline w-3 h-3 mr-1 align-text-bottom" />
                          {ev.admin_moderation.reason}
                        </p>
                      )}
                    </td>
                    <td className="px-4 py-3 align-top text-[#4A5D50]">{ev.organiser_name || '—'}</td>
                    <td className="px-4 py-3 align-top text-[#4A5D50]">{ev.date}</td>
                    <td className="px-4 py-3 align-top"><StatusBadge status={ev.status || 'active'} /></td>
                    <td className="px-4 py-3 align-top">
                      <span className={`text-xs ${ev.has_sales ? 'text-emerald-700 font-medium' : 'text-stone-500'}`}>
                        {ev.orders_count || 0}
                      </span>
                    </td>
                    <td className="px-4 py-3 align-top text-right">
                      <div className="flex flex-wrap gap-1 justify-end">
                        {ACTIONS.map(({ key, label, icon: Icon, danger }) => {
                          // Hide actions that don't make sense for the current status
                          if (key === 'restore' && (ev.status === 'active' || !ev.admin_moderation)) return null;
                          if (key !== 'restore' && (ev.status || 'active') === STATUS_META[key === 'suspend' ? 'suspended' : key === 'unpublish' ? 'unpublished' : key === 'archive' ? 'archived' : key === 'cancel' ? 'cancelled' : key]?.label?.toLowerCase()?.replace(' ', '_')) return null;
                          return (
                            <button
                              key={key}
                              onClick={() => { setActionFor({ event: ev, action: key }); setReason(''); }}
                              className={`inline-flex items-center px-2.5 py-1 rounded-md border text-[11px] font-medium transition-colors ${
                                key === 'restore'
                                  ? 'border-emerald-300 text-emerald-700 hover:bg-emerald-50'
                                  : danger
                                  ? 'border-rose-200 text-rose-700 hover:bg-rose-50'
                                  : 'border-amber-200 text-amber-700 hover:bg-amber-50'
                              }`}
                              title={label}
                              data-testid={`admin-event-action-${key}-${ev.event_id}`}
                            >
                              <Icon className="w-3 h-3 mr-1" /> {label}
                            </button>
                          );
                        })}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Reason / confirmation dialog */}
      <Dialog open={!!actionFor} onOpenChange={(o) => !o && !submitting && setActionFor(null)}>
        <DialogContent data-testid="admin-event-action-dialog">
          {actionFor && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-[#112318]">
                  {(() => {
                    const meta = ACTIONS.find((a) => a.key === actionFor.action);
                    const Icon = meta?.icon || ShieldAlert;
                    return <><Icon className="w-5 h-5 text-rose-600" /> {meta?.label}</>;
                  })()}
                </DialogTitle>
                <DialogDescription>
                  {ACTIONS.find((a) => a.key === actionFor.action)?.description} <br />
                  Event: <strong>{actionFor.event.title}</strong>
                  {actionFor.event.orders_count > 0 && (
                    <span className="block text-[11px] text-amber-700 mt-1">
                      This event has {actionFor.event.orders_count} completed order(s) — existing tickets and payment records will be preserved.
                    </span>
                  )}
                </DialogDescription>
              </DialogHeader>
              {actionFor.action !== 'restore' && (
                <div className="space-y-2 mt-1">
                  <Label htmlFor="moderation-reason">
                    Reason {['suspend', 'unpublish', 'under_review', 'archive', 'cancel'].includes(actionFor.action) ? '*' : ''}
                  </Label>
                  <Textarea
                    id="moderation-reason"
                    rows={3}
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="e.g. Suspicious activity / Misleading details / Payment concern…"
                    data-testid="admin-event-action-reason"
                  />
                  <p className="text-[11px] text-stone-500">
                    Stored in the audit log and included in the email to the organiser.
                  </p>
                </div>
              )}
              <DialogFooter className="mt-3 gap-2">
                <Button variant="outline" onClick={() => setActionFor(null)} disabled={submitting}>Cancel</Button>
                <Button
                  onClick={submitAction}
                  disabled={submitting}
                  className={actionFor.action === 'restore'
                    ? 'bg-emerald-700 hover:bg-emerald-800 text-white'
                    : 'bg-rose-700 hover:bg-rose-800 text-white'}
                  data-testid="admin-event-action-confirm"
                >
                  {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Confirm
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminEventsPage;
